import $api from "@/api/index";
import {
  ReferalsResponse,
  ConditionI,
  Earnings,
  RefProgram,
  ServiceType,
  YIdByTgId,
  ReferalsStatus,
  InviteText,
  MultiplierInfo,
  MultiplierReferralsPage,
  DashboardMode,
  DashboardInfo,
  DashboardStep,
  DashboardReferrals,
  UserStatus,
  DashboardReferralDriver,
  BloggerCompetitionResponse,
} from "@/types/referals";

type ReferalProgramsResponse = {
  results: ConditionI[];
};
export async function getReferalPrograms(
  servicetype: ServiceType | ServiceType[],
  yandexId: string
): Promise<ConditionI[]> {
  const res = await $api.get<ReferalProgramsResponse>(
    "/api/referral_program/offers-with-multiplier/",
    {
      params: {
        service_type: servicetype,
        yandex_id: yandexId,
      },
      paramsSerializer: {
        indexes: null,
      },
    }
  );
  return res.data.results;
}

export async function getMultiplierInfo(
  yandexId: string
): Promise<MultiplierInfo> {
  const res = await $api.get<MultiplierInfo>(
    "/api/referral_program/multiplier/",
    { params: { yandex_id: yandexId } }
  );
  return res.data;
}

export async function getMultiplierReferrals(
  yandexId: string,
  limit: number = 10,
  offset: number = 0
): Promise<MultiplierReferralsPage> {
  const res = await $api.get<MultiplierReferralsPage>(
    "/api/referral_program/multiplier/referrals/",
    { params: { yandex_id: yandexId, limit, offset } }
  );
  return res.data;
}

export async function getRefProgramById(
  id: number | string,
  yandexId?: string
): Promise<RefProgram> {
  const res = await $api.get(`/api/referral_program/offers/${id}`, {
    params: { yandex_id: yandexId },
  });
  return res.data;
}

export async function getReferals(
  yandexId: string,
  referalsStatus: ReferalsStatus,
  offset?: number,
  sortType?: string
): Promise<ReferalsResponse> {
  const refCount = offset || 0;
  const sort = sortType ? `&sort=${sortType}` : "";

  const res = await $api.get(
    `/api/referral_program/referrals/?yandex_id=${yandexId}&filter=${referalsStatus}&limit=10&offset=${refCount}${sort}`
  );
  return res.data;
}

export async function getEarningsData(yandexId: string): Promise<Earnings> {
  const res = await $api.get(
    `/api/referral_program/earnings/?yandex_id=${yandexId}`
  );
  return res.data;
}

export async function withdrawEarnings(yandexId: string) {
  const res = await $api.post(
    `/api/referral_program/withdraw/?yandex_id=${yandexId}`
  );
  return res.data;
}

export async function getYIdByTgId(telegramId: string): Promise<YIdByTgId> {
  const res = await $api.get(
    `/api/persons/get-driver-by-telegram-id/?telegram_id=${telegramId}`
  );
  return res.data;
}

export async function getInviteShareText(): Promise<InviteText> {
  const res = await $api.get(`/api/referral_program/invite_texts/`);
  return res.data;
}

export async function getDashboardData(
  yandexId: string,
  dateFrom: string,
  dateTo: string,
  mode: DashboardMode,
  tariff?: string[] | null,
  serviceType?: string
): Promise<DashboardInfo> {
  const servType = serviceType ? `&service_type=${serviceType}` : "";
  const tar = tariff?.length && tariff ? `&tariff=${tariff}` : "";

  const res = await $api.get(
    `/api/referral_program/dashboard/?yandex_id=${yandexId}&date_from=${dateFrom}&date_to=${dateTo}&mode=${mode}${servType}${tar}`
  );
  return res.data;
}

export async function getDashboardReferals(
  yandexId: string,
  dateFrom: string,
  dateTo: string,
  mode: DashboardMode,
  stepType: DashboardStep,
  offset?: number,
  sortType?: string,
  tariff?: string[] | null,
  serviceType?: string
): Promise<DashboardReferrals> {
  const servType = serviceType ? `&service_type=${serviceType}` : "";
  const tar = tariff?.length && tariff ? `&tariff=${tariff}` : "";
  const refCount = offset || 0;
  const sort = sortType ? `&sort=${sortType}` : "";

  const res = await $api.get(
    `/api/referral_program/dashboard/referrals/?yandex_id=${yandexId}&date_from=${dateFrom}&date_to=${dateTo}&mode=${mode}${servType}${tar}&step=${stepType}&limit=10&offset=${refCount}${sort}`
  );
  return res.data;
}

export async function getUserStatus(
  yandexId: string,
  serviceType: ServiceType
): Promise<UserStatus> {
  const res = await $api.get<UserStatus>("/api/referral_program/me/", {
    params: { yandex_id: yandexId, service_type: serviceType },
  });
  return res.data;
}

export async function getEatsLinkUnlocked(
  yandexId: string,
  serviceType: ServiceType
): Promise<boolean> {
  const res = await $api.get<{ eats_link_unlocked: boolean }>(
    "/api/referral_program/eats-link/",
    { params: { yandex_id: yandexId, service_type: serviceType } }
  );
  return res.data.eats_link_unlocked;
}

export async function getBloggerCompetition(
  yandexId: string,
  serviceType: ServiceType
): Promise<BloggerCompetitionResponse> {
  const res = await $api.get<BloggerCompetitionResponse>(
    "/api/referral_program/blogger-competition/",
    { params: { yandex_id: yandexId, service_type: serviceType } }
  );
  return res.data;
}

export async function getDashboardReferalDriver(
  referralDriverId: number,
  yandexId: string,
  dateFrom: string,
  dateTo: string,
  mode: DashboardMode,
  tariff?: string[] | null,
  serviceType?: string
): Promise<DashboardReferralDriver> {
  const servType = serviceType ? `&service_type=${serviceType}` : "";
  const tar = tariff?.length && tariff ? `&tariff=${tariff}` : "";

  const res = await $api.get(
    `/api/referral_program/dashboard/referrals/${referralDriverId}/?yandex_id=${yandexId}&date_from=${dateFrom}&date_to=${dateTo}&mode=${mode}${servType}${tar}`
  );
  return res.data;
}

export async function sendFiveWays(yandexId: string) {
  const res = await $api.post(
    `/api/referral_program/referral-invite-tips-alert/?yandex_id=${yandexId}`
  );
  return res.data;
}
